import { Boundaries } from "@planzone/core";
import {
  PersonalTimeSheet,
  personalTimeSheet,
  TimeSheet,
  timeSheet,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceList } from "../../common/list";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchRequest, mergeTS } from "./utils";
import { reduceProject } from "../../common/project";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    resource,
    filter,
    size,
    page,
  }: Boundaries["retrievePersonalTimeSheet"]["request"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          filter,
          request: setRequestStateToPending(),
          timeSheet: personalTimeSheet,
          size,
          currentPage: page,
          canLoadMore: true,
          isDirty: false,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    resource,
    filter,
    timeSheet,
    page,
    size,
  }: Boundaries["retrievePersonalTimeSheet"]["success"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          resource,
          filter,
          request: setRequestStateToSuccess(),
          timeSheet:
            page === 0
              ? timeSheet
              : {
                  ...state[index].timeSheet,
                  ...timeSheet,
                  projects: [
                    ...state[index].timeSheet.projects.slice(0, page * size),
                    ...timeSheet.projects,
                    ...state[index].timeSheet.projects.slice(
                      page * size + size,
                    ),
                  ],
                },
          currentPage: page + 1,
          canLoadMore: timeSheet.projects.length >= size,
          isDirty: false,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          filter,
          size,
          currentPage: page + 1,
          request: setRequestStateToSuccess(),
          timeSheet,
          canLoadMore: timeSheet.projects.length >= size,
          isDirty: false,
        },
      ];
}

function handleFailure(
  state: State,
  {
    resource,
    filter,
    size,
    page,
  }: Boundaries["retrievePersonalTimeSheet"]["failure"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, resource, filter, size),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          resource,
          filter,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          filter,
          size,
          currentPage: page,
          request: setRequestStateToFailure(),
          timeSheet: personalTimeSheet,
          canLoadMore: true,
          isDirty: false,
        },
      ];
}

function handleAllocationResources(
  state: State,
  { allocations, project }: Boundaries["allocateResources"]["success"],
): State {
  return state.map((req) => {
    const isAffected = allocations.some(
      (allocation) => allocation.resource.uuid === req.resource.uuid,
    );

    if (!isAffected) return req;

    const dirtyReq = { ...req, isDirty: true };

    if (dirtyReq.timeSheet.projects.some((p) => p.uuid === project.uuid)) {
      return dirtyReq;
    }

    return {
      ...dirtyReq,
      timeSheet: {
        ...dirtyReq.timeSheet,
        projects: [
          ...dirtyReq.timeSheet.projects,
          {
            ...project,
            integratedAt: "",
            deactivated: false,
            totalOfAllocatedHours: 0,
            totalOfSpentTime: 0,
            totalOfSpentTimeInWeek: 0,
            totalOfRemainingTime: 0,
            spentTimes: [],
          },
        ],
      },
    };
  });
}

function handleEditAllocation(
  state: State,
  allocation: Boundaries["editAllocation"]["success"],
): State {
  // The PATCH /allocations response (Spring Data REST) doesn't carry the
  // resource uuid: when it's missing, reload every personal/team timesheet.
  const resourceUuid = allocation.resource?.uuid;

  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || !resourceUuid || req.resource.uuid === resourceUuid,
  }));
}

function handleRemoveAllocation(
  state: State,
  allocation: Boundaries["removeAllocation"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === allocation.resource.uuid,
  }));
}

function handleValidateTimeRecordsUpToDate(
  state: State,
  { resource }: Boundaries["validatePersonalTimeRecordsUpToDate"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === resource.uuid,
  }));
}

function handleDisableProjectResource(
  state: State,
  projectResource: Boundaries["disableProjectResource"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === projectResource.resource.uuid,
  }));
}

function handleActivateProjectResource(
  state: State,
  projectResource: Boundaries["activateProjectResource"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === projectResource.resource.uuid,
  }));
}

function handleEditTimesheetResource(
  state: State,
  { timeRecord }: Boundaries["saveTimeRecord"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === timeRecord.resource.uuid,
  }));
}

function handleDeleteTimeRecord(
  state: State,
  { timeRecord }: Boundaries["deleteTimeRecord"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === timeRecord.resource.uuid,
  }));
}

function handleRemoveResourceFromProject(
  state: State,
  { projectResource }: Boundaries["removeResourceFromProject"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === projectResource.resource.uuid,
  }));
}

function handleRemoveResourceFromPlanzone(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    isDirty: req.isDirty || req.resource.uuid === resource.uuid,
  }));
}

function handleRemoveTask(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleRemoveList(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    timeSheet: {
      ...req.timeSheet,
      resource: {
        ...req.timeSheet.resource,
        ...reduceResource(req.timeSheet.resource, event),
      } as PersonalTimeSheet["resource"],
      projects: req.timeSheet.projects.map((project) => ({
        ...project,
        ...(reduceProject(
          project,
          event,
        ) as PersonalTimeSheet["projects"][number]),
      })),
    },
  }));
}

const reduceState = createStateReducer(
  initialState,
  {
    [events.retrievePersonalTimeSheetRequested]: handleRequest,
    [events.retrievePersonalTimeSheetSucceeded]: handleSuccess,
    [events.retrievePersonalTimeSheetFailed]: handleFailure,
    [events.allocateResourcesSucceeded]: handleAllocationResources,
    [events.editAllocationSucceeded]: handleEditAllocation,
    [events.removeAllocationSucceeded]: handleRemoveAllocation,
    [events.validatePersonalTimeRecordsUpToDateSucceeded]:
      handleValidateTimeRecordsUpToDate,
    // [events.reloadTimeSheetSucceeded]: handleReload,
    [events.disableProjectResourceSucceeded]: handleDisableProjectResource,
    [events.activateProjectResourceSucceeded]: handleActivateProjectResource,
    [events.removeResourceFromProjectSucceeded]:
      handleRemoveResourceFromProject,
    [events.removeResourceFromPlanzoneSucceeded]:
      handleRemoveResourceFromPlanzone,
    [events.saveTimeRecordSucceeded]: handleEditTimesheetResource,
    [events.deleteTimeRecordSucceeded]: handleDeleteTimeRecord,
    [events.removeTaskSucceeded]: handleRemoveTask,
    [events.removeListSucceeded]: handleRemoveList,
  },
  propagateEvent,
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
